import { jsonHeaders } from "./api";
import { PHASE_COLORS } from "./journey";
import { clearCached } from "./projectCache";

/**
 * Oppgaver i prosjektplanen (Erik 03.11.2026): en oppgave ER en aktivitet i prosjektplanen
 * (portal.ProjectActivities), med id = ProjectActivityId. Mine oppgaver, oppgavekortet og fasesiden
 * leser og skriver de samme radene. API-et ligger i api/src/functions/oppgaver.ts.
 */

export type Status = "IkkeStartet" | "Pågår" | "Fullført" | "IkkeRelevant";
export const STATUSER: { value: Status; label: string }[] = [
  { value: "IkkeStartet", label: "Ikke startet" },
  { value: "Pågår", label: "Pågår" },
  { value: "Fullført", label: "Fullført" },
  { value: "IkkeRelevant", label: "Ikke relevant" },
];
export const statusNavn = (s: string) => STATUSER.find((x) => x.value === s)?.label ?? s;

export type Radgiver = { id: number; projectId: number; navn: string; firma: string | null; rolle: string | null; epost: string | null; telefon: string | null };

/** FS slutt → start, SS start → start, FF slutt → slutt, SF start → slutt (som i Microsoft Project). */
export type AvhType = "FS" | "SS" | "FF" | "SF";
export const AVH_NAVN: Record<AvhType, string> = { FS: "Slutt → start", SS: "Start → start", FF: "Slutt → slutt", SF: "Start → slutt" };
/** Fra = forgjengeren, til = oppgaven som venter på den. */
export type Avhengighet = { id: number; fraId: number; tilId: number; type: AvhType; fraTittel: string; tilTittel: string };
/** Avhengigheten som en setning: «B» kan starte når «A» er ferdig. */
export function avhSetning(type: AvhType, fra: string, til: string) {
  const naar = type[0] === "F" ? "er ferdig" : "har startet";
  return type[1] === "S" ? `«${til}» kan starte når «${fra}» ${naar}` : `«${til}» kan bli ferdig når «${fra}» ${naar}`;
}

export type Oppgave = {
  id: number;
  projectId: number;
  prosjekt: string;
  phaseId: number;
  faseKode: string;
  fase: string;
  delfase: string | null;
  tittel: string;
  /** Aktivitetens «Kommentar»-felt, som på fasesiden */
  kommentar: string | null;
  status: Status;
  /** Responsible-teksten: brukerens eller rådgiverens navn, eller fritekst fra før */
  ansvarlig: string | null;
  ansvarligId: string | null;
  radgiver: Radgiver | null;
  deltakere: { id: string; navn: string }[];
  /** Startdato (020_oppgave_startdato.sql); null når den ikke er satt eller kolonnen ikke finnes ennå */
  start: string | null;
  frist: string | null;
  /** Egen aktivitet på prosjektet (ikke fra malen): tekst, fase og delfase kan endres */
  egen: boolean;
  kilde: "Mal" | "Egen" | "Dokument";
  arsak: string | null;
  kommentarer: number;
  dokumenter: number;
  paminnUke: boolean;
  paminnDag: boolean;
  opprettetAv: string | null;
  endret: string | null;
  sortOrder: number;
  /** Avhengighetene oppgaven er med i, begge veier (tom før 021 er kjørt) */
  avhengigheter: Avhengighet[];
};

export type OppgaveFil = {
  id: number;
  kilde: "prosjekt" | "opplastet";
  navn: string;
  driveId: string | null;
  itemId: string | null;
  url: string | null;
  storrelse: number | null;
  aiStatus: "leser" | "ok" | "feil" | null;
  plassering: string | null;
  tagger: string[];
  oppsummering: string | null;
  lagtTilAv: string | null;
  lagtTil: string;
};

export type OppgaveKort = Oppgave & {
  historikk: { id: number; brukerId: string | null; navn: string | null; tekst: string; tid: string }[];
  filer: OppgaveFil[];
};

export type Meg = { userId: string; displayName: string; email: string };
export type Bruker = { userId: string; displayName: string; email: string };

export class OppgaveFeil extends Error {
  constructor(public status: number, melding: string, public kode?: string) {
    super(melding);
  }
}

async function kall<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, headers: { ...jsonHeaders(), ...(init?.headers ?? {}) } });
  const body = (await res.json().catch(() => null)) as (T & { error?: string; code?: string }) | null;
  if (!res.ok) {
    if (res.status === 404 && !body?.error) throw new OppgaveFeil(404, "Oppgaver er ikke tilgjengelig i portalen ennå", "ikke-klar");
    throw new OppgaveFeil(res.status, body?.error ?? `Feil fra serveren (${res.status})`, body?.code);
  }
  return body as T;
}

/**
 * Siste svar på «Mine oppgaver», delt av Hjem og Mine oppgaver: listen vises med en gang ved
 * sidebytte og oppdateres i bakgrunnen (Erik 03.11.2026: hentingen tok for lang tid).
 */
let mineSist: { meg: Meg; oppgaver: Oppgave[] } | null = null;
export const mineFraMinnet = () => mineSist;
export const hentMine = () => kall<{ meg: Meg; oppgaver: Oppgave[] }>("/api/oppgaver/mine").then((d) => (mineSist = d));
export const hentForProsjekt = (projectId: number) =>
  kall<{ meg: Meg; oppgaver: Oppgave[]; radgivere: Radgiver[] }>(`/api/projects/${projectId}/oppgaver`);

/** Milepæl fra prosjektplanen (portal.Milestones), vist i liste, kalender og Gantt (Erik 04.11.2026). */
export type Milepael = {
  id: number; projectId: number; prosjekt: string; nr: string; navn: string; beslutter: string | null;
  planlagt: string | null; faktisk: string | null; status: "Pending" | "Approved" | "Delayed" | string; notat: string | null;
};
export const MILEPAEL_STATUS: Record<string, string> = { Pending: "Avventer", Approved: "Godkjent", Delayed: "Forsinket" };
/** Datoen milepælen står på: den faktiske når den finnes, ellers den planlagte. */
export const milepaelDato = (m: Milepael) => m.faktisk ?? m.planlagt;
export const hentMilepaler = (prosjekter: number[]) =>
  prosjekter.length
    ? kall<{ milepaler: Milepael[] }>(`/api/oppgaver/milepaler?prosjekter=${[...new Set(prosjekter)].join(",")}`).then((r) => r.milepaler)
    : Promise.resolve([] as Milepael[]);
export const hentKort = (id: number) => kall<OppgaveKort>(`/api/oppgaver/${id}`);

let brukereSist: Promise<Bruker[]> | null = null;
/** Portalbrukerne (ansvarlig og deltakere), hentet én gang per sidevisning. */
export const hentBrukere = () =>
  (brukereSist ??= kall<Bruker[]>("/api/users").catch((e) => {
    brukereSist = null;
    throw e;
  }));

/** Etter en endring: prosjektsiden (fasene, «Forfaller snart») henter prosjektet på nytt. */
const endret = <T extends { projectId: number }>(r: T) => {
  clearCached(r.projectId);
  return r;
};

/** Ansvarlig i skjemaet: «u:<brukerId>», «r:<rådgiverId>» eller "" (ingen). */
export type AnsvarligValg = string;
export const ansvarligValg = (o: Pick<Oppgave, "ansvarligId" | "radgiver">): AnsvarligValg =>
  o.ansvarligId ? `u:${o.ansvarligId}` : o.radgiver ? `r:${o.radgiver.id}` : "";

export type NyOppgave = {
  phaseId: number; delfase: string; tittel: string; ansvarlig: AnsvarligValg | null; deltakere: string[];
  start: string | null; frist: string | null; status: Status; kommentar: string | null;
};
export const opprett = (projectId: number, o: NyOppgave) =>
  kall<OppgaveKort>(`/api/projects/${projectId}/oppgaver`, { method: "POST", body: JSON.stringify(o) }).then(endret);

export type Endring = Partial<{
  tittel: string; kommentar: string | null; status: Status; ansvarlig: AnsvarligValg | null; start: string | null; frist: string | null;
  phaseId: number; delfase: string; paminnUke: boolean; paminnDag: boolean; leggTilDeltaker: string; fjernDeltaker: string;
}>;
export const endre = (id: number, e: Endring) => kall<OppgaveKort>(`/api/oppgaver/${id}`, { method: "PATCH", body: JSON.stringify(e) }).then(endret);
export const slett = (id: number, projectId: number) =>
  kall<{ slettet: number }>(`/api/oppgaver/${id}`, { method: "DELETE" }).then((r) => (clearCached(projectId), r));
export const leggTilAvhengighet = (fraId: number, tilId: number, type: AvhType) =>
  kall<Avhengighet>("/api/oppgaver/avhengigheter", { method: "POST", body: JSON.stringify({ fraId, tilId, type }) });
export const fjernAvhengighet = (id: number) => kall<{ slettet: number }>(`/api/oppgaver/avhengigheter/${id}`, { method: "DELETE" });
export const knyttDokument = (id: number, driveId: string, itemId: string) =>
  kall<OppgaveKort>(`/api/oppgaver/${id}/dokumenter`, { method: "POST", body: JSON.stringify({ driveId, itemId }) });
export const fjernDokument = (dokId: number) => kall<OppgaveKort>(`/api/oppgaver/dokumenter/${dokId}`, { method: "DELETE" });

/** Laster opp en fil på oppgaven. Svaret kommer når AI-en har lest og plassert den. */
export async function lastOpp(id: number, fil: File): Promise<OppgaveKort> {
  const res = await fetch(`/api/oppgaver/${id}/dokumenter/opplast?navn=${encodeURIComponent(fil.name)}`, {
    method: "POST",
    headers: { ...jsonHeaders(), "Content-Type": fil.type || "application/octet-stream" },
    body: fil,
  });
  const body = (await res.json().catch(() => null)) as (OppgaveKort & { error?: string }) | null;
  if (!res.ok || !body) throw new OppgaveFeil(res.status, body?.error ?? `Opplastingen feilet (${res.status})`);
  return body;
}

/** Henter en fil (med innlogging) og åpner den i ny fane, eller laster den ned. */
export async function apneFil(url: string, navn: string, lastNed = false) {
  const res = await fetch(url, { headers: jsonHeaders() });
  if (!res.ok) throw new OppgaveFeil(res.status, `Kunne ikke hente filen (${res.status})`);
  const blob = await res.blob();
  const lenke = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = lenke;
  if (lastNed || !/^(application\/pdf|image\/)/.test(blob.type)) a.download = navn;
  else a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(lenke), 60_000);
}
export const filUrl = (dokId: number) => `/api/oppgaver/dokumenter/${dokId}/fil`;
export const kalenderUrl = (id: number) => `/api/oppgaver/${id}/kalender.ics`;

export type Opplastning = {
  id: number; oppgaveId: number; oppgave: string; navn: string; storrelse: number | null; aiStatus: string | null;
  plassering: string | null; tagger: string[]; oppsummering: string | null; lagtTilAv: string | null; lagtTil: string;
};
export const hentOpplastninger = (projectId: number) =>
  kall<{ opplastninger: Opplastning[] }>(`/api/projects/${projectId}/oppgaver/opplastninger`).then((r) => r.opplastninger);

// ---------- Eksterne rådgivere (Admin-fanen) ----------

export type RadgiverSkjema = { navn: string; firma: string; rolle: string; epost: string; telefon: string };
export const hentRadgivere = (projectId: number) => kall<{ radgivere: Radgiver[] }>(`/api/projects/${projectId}/radgivere`).then((r) => r.radgivere);
export const leggTilRadgiver = (projectId: number, r: RadgiverSkjema) =>
  kall<{ radgivere: Radgiver[] }>(`/api/projects/${projectId}/radgivere`, { method: "POST", body: JSON.stringify(r) }).then((x) => x.radgivere);
export const endreRadgiver = (id: number, r: Partial<RadgiverSkjema>) =>
  kall<{ radgivere: Radgiver[] }>(`/api/radgivere/${id}`, { method: "PATCH", body: JSON.stringify(r) }).then((x) => x.radgivere);
export const fjernRadgiver = (id: number) => kall<{ radgivere: Radgiver[] }>(`/api/radgivere/${id}`, { method: "DELETE" }).then((x) => x.radgivere);

// ---------- Innstillinger og varsler ----------

export type Innstillinger = { paminnUke: boolean; paminnDag: boolean; epost: boolean; epostTilgjengelig: boolean };
export const hentInnstillinger = () => kall<Innstillinger>("/api/oppgaver/innstillinger");
export const lagreInnstillinger = (i: Partial<Innstillinger>) =>
  kall<Innstillinger>("/api/oppgaver/innstillinger", { method: "PUT", body: JSON.stringify(i) });

export type ServerVarsel = { id: number; type: string; tekst: string; oppgaveId: number | null; projectId: number | null; lest: boolean; opprettet: string };
export const hentVarsler = () => kall<{ varsler: ServerVarsel[] }>("/api/varsler").then((r) => r.varsler);
export const merkVarslerLest = (b: { id?: number; oppgaveId?: number } = {}) =>
  kall<{ ok: true }>("/api/varsler/lest", { method: "POST", body: JSON.stringify(b) });

// ---------- Frister og nøkkeltall ----------

export const iDag = () => new Date().toLocaleDateString("sv-SE"); // ÅÅÅÅ-MM-DD, lokal tid
export function pluss(d: string, n: number) {
  const x = new Date(`${d}T12:00:00`);
  x.setDate(x.getDate() + n);
  return x.toLocaleDateString("sv-SE");
}
export const dagerMellom = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 864e5);
/** Søndag i inneværende uke (uken går mandag–søndag). */
export function ukeslutt(d = iDag()) {
  const dag = new Date(`${d}T12:00:00`).getDay(); // 0 = søndag
  return pluss(d, dag === 0 ? 0 : 7 - dag);
}
/** «Forfaller snart»: frist de neste 14 dagene (Erik 03.11.2026), på prosjektet og i Mine oppgaver. */
export const SNART_DAGER = 14;
export const apen = (o: Pick<Oppgave, "status">) => o.status !== "Fullført" && o.status !== "IkkeRelevant";
export const forfalt = (o: Pick<Oppgave, "status" | "frist">, idag = iDag()) => apen(o) && !!o.frist && o.frist < idag;
export const snart = (o: Pick<Oppgave, "status" | "frist">, idag = iDag()) => apen(o) && !!o.frist && o.frist >= idag && o.frist <= pluss(idag, SNART_DAGER);
export const denneUken = (o: Pick<Oppgave, "status" | "frist">, idag = iDag()) => apen(o) && !!o.frist && o.frist >= idag && o.frist <= ukeslutt(idag);
export const sorter = (a: Oppgave, b: Oppgave) =>
  Number(!apen(a)) - Number(!apen(b)) || (a.frist ?? "9999").localeCompare(b.frist ?? "9999") || a.tittel.localeCompare(b.tittel, "nb");

export const fmtDato = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("nb-NO", { day: "numeric", month: "short" });
export const fmtTid = (iso: string) =>
  new Date(iso).toLocaleString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** Fasens farge (som i fasereisen), grå uten fase. */
export const faseFarge = (kode: string | null) => (kode && PHASE_COLORS[kode]) || { main: "var(--kat-grey)", soft: "var(--kat-grey-soft)" };

/** Rolle for meg på oppgaven. */
export const minRolle = (o: Oppgave, meg: Meg | null) =>
  !meg ? null : o.ansvarligId === meg.userId ? "ansvarlig" : o.deltakere.some((d) => d.id === meg.userId) ? "deltaker" : null;
